import {
  createContext,
  useContext,
  useState,
  ReactNode,
} from 'react';

type Locale = 'fr' | 'en';

const translations = {
  fr: {
    login: {
      title: 'Espace éditeur',
      subtitle:
        'Connectez-vous avec vos identifiants API pour gérer votre abonnement.',
      clientId: 'Client ID',
      clientSecret: 'Client Secret',
      submit: 'Se connecter',
      loading: 'Connexion…',
      error:
        'Identifiants invalides. Vérifiez votre client_id et client_secret.',
    },
    dashboard: {
      title: 'Mon abonnement',
      editor: 'Éditeur',
      status: 'Statut',
      seats: 'Écoles actives (sièges facturés)',
      noStripe:
        "La facturation n'est pas encore activée pour cet environnement.",
      subscribe: 'Souscrire',
      manage: "Gérer l'abonnement",
      logout: 'Déconnexion',
      loadingCtx: "Chargement de l'abonnement…",
      errorCtx: "Impossible de charger les données d'abonnement.",
      statusLabel: {
        none: 'Aucun abonnement',
        active: 'Actif',
        trialing: "Période d'essai",
        past_due: 'Paiement en retard',
        canceled: 'Résilié',
        unpaid: 'Impayé',
      },
    },
    stats: {
      title: 'Utilisation de l\'API',
      totalRequests: 'Requêtes totales',
      byStatus: 'Par statut',
      webhooks: 'Webhooks',
      noData: 'Aucune donnée disponible pour le moment.',
      loading: 'Chargement des statistiques…',
    },
    common: {
      poweredBy: 'Permio API',
    },
  },
  en: {
    login: {
      title: 'Editor Portal',
      subtitle:
        'Sign in with your API credentials to manage your subscription.',
      clientId: 'Client ID',
      clientSecret: 'Client Secret',
      submit: 'Sign in',
      loading: 'Signing in…',
      error:
        'Invalid credentials. Please check your client_id and client_secret.',
    },
    dashboard: {
      title: 'My subscription',
      editor: 'Editor',
      status: 'Status',
      seats: 'Active schools (billed seats)',
      noStripe: 'Billing is not yet enabled for this environment.',
      subscribe: 'Subscribe',
      manage: 'Manage subscription',
      logout: 'Log out',
      loadingCtx: 'Loading subscription…',
      errorCtx: 'Unable to load subscription data.',
      statusLabel: {
        none: 'No subscription',
        active: 'Active',
        trialing: 'Trial period',
        past_due: 'Payment overdue',
        canceled: 'Canceled',
        unpaid: 'Unpaid',
      },
    },
    stats: {
      title: 'API Usage',
      totalRequests: 'Total requests',
      byStatus: 'By status',
      webhooks: 'Webhooks',
      noData: 'No data available yet.',
      loading: 'Loading statistics…',
    },
    common: {
      poweredBy: 'Permio API',
    },
  },
} as const;

type Translations = (typeof translations)[Locale];

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  tx: Translations;
}

const I18nContext = createContext<I18nContextValue>({
  locale: 'fr',
  setLocale: () => {},
  tx: translations.fr,
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const saved = (localStorage.getItem('ep-locale') as Locale | null) ?? 'fr';
  const [locale, setLocaleState] = useState<Locale>(saved);

  function setLocale(l: Locale) {
    localStorage.setItem('ep-locale', l);
    setLocaleState(l);
  }

  return (
    <I18nContext.Provider value={{ locale, setLocale, tx: translations[locale] }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
